import React, { useEffect, useState } from "react";

// Lazy-load so it doesn't bloat initial bundle
async function importTools() {
  const [html2canvasMod, jsPDFMod] = await Promise.all([
    import("html2canvas"),
    import("jspdf"),
  ]);
  return { html2canvas: html2canvasMod.default, jsPDF: jsPDFMod.jsPDF };
}

export default function ChartExportMenu({ targetRef, filename = "chart" }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const close = () => setOpen(false);
    if (open) document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  const btn = {
    padding: "8px 12px",
    borderRadius: 8,
    border: "1px solid #1f2834",
    background: "#0f141c",
    color: "white",
    cursor: "pointer",
  };
  const menuWrap = { position: "relative", display: "inline-block" };
  const menu = {
    position: "absolute",
    top: "100%",
    right: 0,
    background: "#0f141c",
    border: "1px solid #1f2834",
    borderRadius: 8,
    padding: 6,
    minWidth: 140,
    zIndex: 30,
    boxShadow: "0 8px 20px rgba(0,0,0,0.4)",
  };
  const item = {
    padding: "8px 10px",
    color: "#e6edf3",
    cursor: "pointer",
    borderRadius: 6,
  };

  const ensureNode = () =>
    targetRef?.current
      ? targetRef.current
      : targetRef instanceof HTMLElement
      ? targetRef
      : null;

  async function captureCanvas() {
    const el = ensureNode();
    if (!el) throw new Error("Chart container not found");
    const { html2canvas } = await importTools();
    return html2canvas(el, {
      scale: 2,
      backgroundColor: "#0f141c",
      useCORS: true,
    });
  }

  const exportPNG = async () => {
    try {
      const canvas = await captureCanvas();
      const link = document.createElement("a");
      link.download = `${filename}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      setOpen(false);
    } catch (e) {
      console.error(e);
    }
  };

  const exportPDF = async () => {
    try {
      const [{ jsPDF }, canvas] = await Promise.all([
        importTools(),
        captureCanvas(),
      ]);
      const imgData = canvas.toDataURL("image/png");
      const w = canvas.width, h = canvas.height;
      const doc = new jsPDF(w > h ? "landscape" : "portrait", "pt", [w, h]);
      doc.addImage(imgData, "PNG", 0, 0, w, h);
      doc.save(`${filename}.pdf`);
      setOpen(false);
    } catch (e) {
      console.error(e);
    }
  };

  // ---- PRINT via hidden iframe (robust across browsers/pop-up blockers)
  const doPrint = async () => {
    try {
      const canvas = await captureCanvas();
      const url = canvas.toDataURL("image/png");

      // Create a same-origin hidden iframe
      const iframe = document.createElement("iframe");
      iframe.style.position = "fixed";
      iframe.style.right = "0";
      iframe.style.bottom = "0";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "0";
      document.body.appendChild(iframe);

      const doc = iframe.contentWindow?.document || iframe.contentDocument;
      doc.open();
      doc.write(`<!doctype html>
        <html>
          <head>
            <meta charset="utf-8" />
            <title>${filename}</title>
            <style>
              @page { margin: 12mm; }
              html,body { margin: 0; background: #0f141c; }
              img { display: block; margin: 0 auto; max-width: 100%; }
            </style>
          </head>
          <body>
            <img id="chartImg" src="${url}" alt="chart"/>
          </body>
        </html>`);
      doc.close();

      const img = doc.getElementById("chartImg");
      img.onload = () => {
        try {
          iframe.contentWindow?.focus();
          // A tiny delay helps Safari / some Chromium builds render before print
          setTimeout(() => iframe.contentWindow?.print(), 50);
          setTimeout(() => { try { iframe.remove(); } catch {} }, 1000);
        } catch {}
      };

      setOpen(false);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div style={menuWrap} onClick={(e) => e.stopPropagation()}>
      <button style={btn} onClick={() => setOpen((o) => !o)}>Export ▾</button>
      {open && (
        <div style={menu}>
          <div style={item} onClick={exportPNG}>PNG</div>
          <div style={item} onClick={exportPDF}>PDF</div>
          <div style={item} onClick={doPrint}>Print</div>
        </div>
      )}
    </div>
  );
}
